import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import SelectInput from '@/components/forms/SelectInput';
import { Collapse } from '@mui/material';
import Container from '../../../../components/Container';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { useStore } from '../../../../components/store/Store';
import { getToggleFieldsSettings, settingsAreaStyle } from './EmployeesAutoBlock';
import { useControlledFieldArray } from '../../../../hooks/useControlledFieldArray';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { EmployeesBlock } from '../../../../declarations/models/blocks/EmployeesBlock';
import DropIndicatorArrow from '../../../../components/DropIndicatorArrow';
import { Employee } from '../../../../declarations/models/Employee';
import FinderButton, { FinderType } from '../../../../components/Finder/FinderButton';
import EmployeesBlockItem from './EmployeesBlockItem';
import { GenericMedia } from '../../../../declarations/GenericMedia';
import SwitchInput from '../../../../components/forms/SwitchInput';
import { OutlinedContainer } from '../../../../components/OutlinedContainer';

export const EmployeesManualBlock: BlockComponent = () => {
  const { t } = useTranslation('components');
  const { state } = useStore();
  const siteId = state.selectedSite?.id;
  const { blockPath, block } = useCurrentBlock();
  const currentBlock = block as EmployeesBlock;
  const mobileGridOptions = ['auto', 'horizontal', 'list']; // 'card' option is intentionally left out
  const mobileListOptions = ['auto', 'card'];

  const {
    fields: selectedEmployees,
    append,
    insert,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<EmployeesBlock>, `${BlockPath}.items`, 'itemHash'>({
    name: `${blockPath}.items`,
  });

  const handleAddEmployee = (item: Employee | null) => {
    if (item?.id) append({ id: item.id });
  };

  return (
    <Container column top gap={2} left fullWidth>
      <Container column gap={2} fullWidth sx={settingsAreaStyle}>
        <Container mb={1} fullWidth>
          <FinderButton
            type={FinderType.EMPLOYEE}
            finderProps={{
              onSelectionConfirmed: (items: Array<GenericMedia<Employee>>) =>
                items.forEach((item) => handleAddEmployee(item.source)),
            }}
          />
        </Container>
        <Container column fullWidth gap={0}>
          {(selectedEmployees || []).map((item, i) => (
            <Fragment key={item.id}>
              <Collapse sx={{ width: '100%' }}>
                <DropIndicatorArrow
                  label={t('Draggable.MoveHere')}
                  borderStyle='dashed'
                  typographyProps={{
                    variant: 'caption',
                    style: {
                      lineHeight: '1rem',
                      fontStyle: 'italic',
                    },
                  }}
                  spacing={2}
                  visible
                  noArrow
                />
              </Collapse>
              <EmployeesBlockItem
                index={i}
                onReorder={move}
                onAdd={(itemToAdd, addAtIndex) => itemToAdd && insert(addAtIndex, itemToAdd)}
                onRemove={remove}
                siteId={siteId}
                itemPathPrefix={`${blockPath}.items`}
              />
            </Fragment>
          ))}
        </Container>
      </Container>
      {getToggleFieldsSettings(t, blockPath)}
      <OutlinedContainer fullWidth column left label={t('KPBlock.DisplayOptionsModuleLabel')}>
        <Container my={1} fullWidth left gap={2}>
          {currentBlock.view?.includes('grid') && (
            <SelectInput
              hideNoSelectionOption
              defaultValue='auto'
              options={mobileGridOptions}
              getOptionKey={(o) => `mobileGrid-${o}`}
              getOptionLabel={(o) => t(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
              path={`${blockPath}.gridMobileBehaviour`}
              label={t('CardModuleBlock.GridMobileBehaviourLabel')}
            />
          )}

          <SwitchInput path={`${blockPath}.enableSearch`} label={t('EmployeesBlock.EnableSearch')} />

          {currentBlock.enableSearch && (
            <SelectInput
              options={['local', 'page', 'site']}
              getOptionKey={(o) => o}
              getOptionLabel={(o) => t(`EmployeesBlock.SearchTypeOptions.${o}`)}
              path={`${blockPath}.searchType`}
              label={t('EmployeesBlock.SearchTypeLabel')}
              defaultValue='local'
              size='small'
              hideNoSelectionOption
            />
          )}

          {currentBlock.view?.includes('_____list') && (
            <SelectInput
              hideNoSelectionOption
              defaultValue='auto'
              options={mobileListOptions}
              getOptionKey={(o) => `mobileGrid-${o}`}
              getOptionLabel={(o) => t(`CardModuleViewTypeSelector.CardModuleViewTypeVariant.${o}`)}
              path={`${blockPath}.gridMobileBehaviour`}
              label={t('CardModuleBlock.ListMobileBehaviourLabel')}
            />
          )}
        </Container>
      </OutlinedContainer>
    </Container>
  );
};

export default EmployeesManualBlock;
